/**
 * Shared session utilities — extracted from HistoryView / AllTasksView.
 */

/**
 * Normalize a media URL from container-internal paths to browser-accessible URLs.
 * Handles /workspace/user-data/ and /workspace/group/ paths from NanoClaw containers.
 */
function normalizeMediaUrl(url) {
  if (typeof url !== 'string') return url;
  // After forcing S3, all media URLs should be https://... absolute paths
  return url;
}

/**
 * Validate that an S3 signed URL is complete (not truncated).
 * Truncated presigned URLs lack the X-Amz-Signature param or end mid-parameter.
 */
function isValidS3Url(url) {
  if (!url || typeof url !== 'string') return false;
  if (!url.includes('amazonaws.com')) return true; // non-S3 URLs pass
  // Signed URLs must have X-Amz-Signature — if missing, URL is truncated
  return url.includes('X-Amz-Signature=') && !url.endsWith('=');
}

/**
 * Extract the first image src from HTML card data in session results.
 * Used as fallback when session photos are unavailable or broken.
 */
function extractCardImages(session) {
  const result = session.result;
  if (!result || typeof result !== 'object') return [];
  const cards = result.cards;
  if (!cards || typeof cards !== 'object') return [];
  const imgs = [];
  for (const card of Object.values(cards)) {
    const html = card.data?.html || card.data?.htmlContent || '';
    if (!html) continue;
    // Extract img src from HTML (nanoclaw file paths or S3 URLs)
    const matches = html.match(/src=["']([^"']+\.(?:jpg|jpeg|png|webp|gif)(?:\?[^"']*)?)['"]/gi);
    if (matches) {
      for (const m of matches) {
        const src = m.match(/src=["']([^"']+)['"]/i)?.[1];
        if (src && !src.startsWith('data:')) imgs.push(src);
      }
    }
  }
  return imgs;
}

/**
 * Extract photo/video URLs from a session's context or prompt.
 * Handles both array and string formats for context.photos,
 * falls back to scanning prompt text for S3 URLs,
 * and finally extracts images from result card HTML.
 * All paths are normalized to browser-accessible URLs.
 */
export function extractSessionPhotos(session) {
  const photos = [];
  if (session.context?.photos) {
    if (Array.isArray(session.context.photos)) {
      for (const item of session.context.photos) {
        if (typeof item === 'string') {
          photos.push({ url: item });
        } else if (item && typeof item === 'object' && item.url) {
          photos.push({ url: item.url, width: item.width, height: item.height });
        }
      }
    } else if (typeof session.context.photos === 'string') {
      const urls = session.context.photos.match(/https?:\/\/[^\s]+/g);
      if (urls) photos.push(...urls.map(url => ({ url })));
    }
  }
  // Fallback 1: scan prompt for S3 URLs (only if complete/valid)
  if (photos.length === 0 && session.prompt) {
    const urls = session.prompt.match(/https:\/\/[^\s]*amazonaws\.com\/[^\s]+/g);
    if (urls) {
      for (const url of urls) {
        if (isValidS3Url(url)) photos.push({ url });
      }
    }
  }
  // Fallback 2: extract images from result card HTML (nanoclaw-served files)
  if (photos.length === 0) {
    const cardImgs = extractCardImages(session);
    for (const url of cardImgs.slice(0, 3)) {
      photos.push({ url });
    }
  }
  return photos.map(p => ({ ...p, url: normalizeMediaUrl(p.url) }));
}
